import { useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { FirmDailyNavCompanyConfigRow, FirmDailyNavConfigVersion } from 'api';
import { firmDailyNavUrl, firmFeatureSettingsUrl } from 'common/config/urls';
import {
  buildDailyNavCompanyConfigDiff,
  buildDailyNavFirmConfigDiff,
  DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING,
  dailyNavApplyDatesLabel,
  dailyNavApplyRefusalMessage,
  dailyNavMarketTimezone,
  dailyNavNextDayLabel,
  formatDailyNavReportSendTime,
  monitoringModeLabel,
  selectDailyNavApplyDates,
  tradingMarketLabel,
} from 'common/constants/dailyNav';
import DailyNavConfigDiff from 'components/DailyNavConfigDiff';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogWarning,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Text,
} from 'components/ui';
import { useApplyDailyNavConfig, useGetDailyNavConfigApplyPreview } from 'services/hooks/dailyNav';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import { CompanySetupStatus, companySetupStatus } from '../dailyNavSetup.utils';
import { DailyNavSetupSessionChanges } from '../hooks/useSetupSessionChanges';

type ReviewStepProps = {
  firmId: number;
  firmSlug: string;
  firmConfig: FirmDailyNavConfigVersion;
  companies: FirmDailyNavCompanyConfigRow[];
  canEdit: boolean;
  isDailyNavEnabled: boolean;
  changes: DailyNavSetupSessionChanges;
  onSelectCompany: (companyId: number) => void;
};

const ReviewStep = ({
  firmId,
  firmSlug,
  firmConfig,
  companies,
  canEdit,
  isDailyNavEnabled,
  changes,
  onSelectCompany,
}: ReviewStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const { applyDailyNavConfig, isPending: isApplyPending } = useApplyDailyNavConfig();
  const [isApplyConfirmOpen, setIsApplyConfirmOpen] = useState(false);
  const { hasDates, applicableDates, recordCount, marketOrWindowsChange } = useMemo(
    () => selectDailyNavApplyDates(applyPreview?.dates),
    [applyPreview?.dates]
  );
  // One firm-wide apply covers every change: only records behind their open config move.
  const canApplyNow = applicableDates.length > 0 && recordCount > 0;
  const applyRefusal = useMemo(() => dailyNavApplyRefusalMessage(applyPreview?.dates), [applyPreview?.dates]);

  const pending = companies.filter(row => companySetupStatus(row) === CompanySetupStatus.NEEDS_ALEXANDRIA);
  const readyCount = companies.filter(row => companySetupStatus(row) === CompanySetupStatus.READY).length;
  const excludedCount = companies.filter(row => companySetupStatus(row) === CompanySetupStatus.EXCLUDED).length;
  const isLive = companies.some(row => Boolean(row.applied_nav_date));

  const changedCompanies = Object.entries(changes.companies);
  const hasChanges = Boolean(changes.firm) || changedCompanies.length > 0;

  const handleApply = async () => {
    setIsApplyConfirmOpen(false);
    if (!firmConfig.id) {
      return;
    }
    const response = await applyDailyNavConfig(firmId, firmConfig.id);
    if (!response) {
      return;
    }
    if (response.reanchored_count) {
      enqueueSnackbar(
        `Applied to ${dailyNavApplyDatesLabel(applicableDates)}: re-pointed ${response.reanchored_count} record${
          response.reanchored_count === 1 ? '' : 's'
        }`,
        { variant: 'success' }
      );
      return;
    }
    const refusal = dailyNavApplyRefusalMessage(response.dates);
    enqueueSnackbar(refusal || 'Nothing needed applying — records already reflect the saved settings', {
      variant: refusal ? 'warning' : 'info',
    });
  };

  let statusAlert = (
    <Alert severity="success">Daily NAV is set up. The first NAV day will generate automatically.</Alert>
  );
  // Generation skips a disabled firm and excluded companies, so neither may be told it will run.
  if (!isDailyNavEnabled) {
    statusAlert = (
      <Alert severity="warning">
        Daily NAV is turned off for this firm, so nothing will generate until it is turned on in Feature Settings.
      </Alert>
    );
  } else if (!companies.length) {
    statusAlert = (
      <Alert severity="info">This firm has no companies yet. Add companies, then set them up here.</Alert>
    );
  } else if (readyCount + pending.length === 0) {
    statusAlert = (
      <Alert severity="warning">Every company is excluded, so no NAV records will be generated.</Alert>
    );
  } else if (pending.length) {
    statusAlert = (
      <Alert severity="warning">
        {pending.length} included compan{pending.length === 1 ? 'y still needs' : 'ies still need'} an external company
        profile. They will be generated, but without market data until they are linked.
      </Alert>
    );
  } else if (isLive) {
    statusAlert = <Alert severity="success">Every included company is set up.</Alert>;
  }

  return (
    <Stack spacing={3} data-testid={DAILY_NAV_SETUP_TEST_IDS.review}>
      {statusAlert}

      {hasChanges && (
        <Paper variant="outlined" sx={{ p: 3 }} data-testid={DAILY_NAV_SETUP_TEST_IDS.sessionChanges}>
          <Text variant="h4" sx={{ mb: 2 }}>
            Changes saved in this session
          </Text>
          {changes.firm && (
            <Box sx={{ mb: 2 }}>
              <Text variant="body2" fontWeight="bold" gutterBottom>
                Firm settings
              </Text>
              {changes.firm.before ? (
                <DailyNavConfigDiff
                  rows={buildDailyNavFirmConfigDiff(changes.firm.before, changes.firm.after)}
                  fromLabel="Before"
                  toLabel="Now"
                  emptyMessage="Saved with no field-level changes."
                />
              ) : (
                <Text variant="body2" color="text.secondary">
                  First firm settings saved.
                </Text>
              )}
            </Box>
          )}
          {changedCompanies.map(([companyId, change], index) => (
            <Box key={companyId}>
              {(index > 0 || changes.firm) && <Divider sx={{ my: 2 }} />}
              <Text variant="body2" fontWeight="bold" gutterBottom>
                {change.companyName}
              </Text>
              {change.before ? (
                <DailyNavConfigDiff
                  rows={buildDailyNavCompanyConfigDiff(change.before, change.after)}
                  fromLabel="Before"
                  toLabel="Now"
                  emptyMessage="Saved with no field-level changes."
                />
              ) : (
                <Text variant="body2" color="text.secondary">
                  First configuration saved.
                </Text>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {canApplyNow && (
        <Alert
          severity="warning"
          action={
            canEdit ? (
              <Button
                size="small"
                onClick={() => setIsApplyConfirmOpen(true)}
                disabled={isApplyPending}
                data-testid={DAILY_NAV_SETUP_TEST_IDS.applyButton}>
                Apply now
              </Button>
            ) : undefined
          }>
          <Text variant="body2">
            {dailyNavApplyDatesLabel(applicableDates)} is still in progress under earlier settings. Apply now to
            re-point its {recordCount} record{recordCount === 1 ? '' : 's'} onto the saved settings. This cannot be
            undone. Otherwise, {dailyNavNextDayLabel(applyPreview?.next_nav_date).toLowerCase()}.
          </Text>
          {marketOrWindowsChange && (
            <Text variant="body2" sx={{ mt: 1 }}>
              {DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING}
            </Text>
          )}
        </Alert>
      )}
      {!canApplyNow && hasChanges && hasDates && applyRefusal && (
        <Alert severity="info">{applyRefusal}</Alert>
      )}
      {!canApplyNow && hasChanges && isLive && !applyRefusal && (
        <Alert severity="info">
          The NAV day in progress already uses the saved settings. {dailyNavNextDayLabel(applyPreview?.next_nav_date)}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Text variant="h4" gutterBottom>
          Firm
        </Text>
        <Text variant="body2">Trading market: {tradingMarketLabel(firmConfig.trading_calendar)}</Text>
        <Text variant="body2">Monitored by: {monitoringModeLabel(firmConfig.monitoring_mode)}</Text>
        <Text variant="body2">
          Reminder time: {formatDailyNavReportSendTime(firmConfig.report_send_time)}{' '}
          {dailyNavMarketTimezone(firmConfig.trading_calendar)}
        </Text>
        <Text variant="body2">
          Reminder recipients:{' '}
          {Array.isArray(firmConfig.notification_recipients) && firmConfig.notification_recipients.length
            ? (firmConfig.notification_recipients as string[]).join(', ')
            : 'None yet'}
        </Text>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Text variant="h4" gutterBottom>
          Companies
        </Text>
        <Text variant="body2">
          {readyCount} ready · {pending.length} need an external company profile · {excludedCount} excluded
        </Text>
        {pending.length > 0 && (
          <List dense>
            {pending.map(row => (
              <ListItem
                key={row.company_id}
                secondaryAction={
                  <Button
                    size="small"
                    onClick={() => onSelectCompany(row.company_id)}
                    data-testid={DAILY_NAV_SETUP_TEST_IDS.reviewSetUpCompanyButton(row.company_id)}>
                    Set up
                  </Button>
                }>
                <ListItemText primary={row.company_name} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      {/* Same confirm step as Daily NAV Settings and Companies: re-pointing can't be undone. */}
      <Dialog
        open={isApplyConfirmOpen}
        onClose={isApplyPending ? undefined : () => setIsApplyConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid={DAILY_NAV_SETUP_TEST_IDS.applyConfirmDialog}>
        <DialogHeader>
          <DialogTitle>Apply to the NAV day in progress?</DialogTitle>
          <DialogCloseButton
            aria-label="Close"
            size="small"
            onClick={() => setIsApplyConfirmOpen(false)}
            disabled={isApplyPending}>
            <DialogCloseIcon />
          </DialogCloseButton>
        </DialogHeader>
        <DialogContent>
          <DialogDescription>
            This re-points {recordCount} record{recordCount === 1 ? '' : 's'} for{' '}
            {dailyNavApplyDatesLabel(applicableDates)} onto the saved settings.
          </DialogDescription>
          <DialogWarning>This cannot be undone.</DialogWarning>
          {marketOrWindowsChange && (
            <Text variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING}
            </Text>
          )}
        </DialogContent>
        <DialogActionFooter
          actionLabel="Apply now"
          cancelLabel="Next NAV day only"
          onAction={handleApply}
          onCancel={() => setIsApplyConfirmOpen(false)}
          isActionLoading={isApplyPending}
          isActionDisabled={isApplyPending}
          actionButtonTestId={DAILY_NAV_SETUP_TEST_IDS.applyConfirmButton}
        />
      </Dialog>
      <Stack direction="row" spacing={2}>
        {isDailyNavEnabled ? (
          <Button<typeof RouterLink>
            variant="contained"
            component={RouterLink}
            to={firmDailyNavUrl(firmSlug)}
            data-testid={DAILY_NAV_SETUP_TEST_IDS.goToDailyNavButton}>
            Go to Daily NAV
          </Button>
        ) : (
          <Button<typeof RouterLink>
            variant="contained"
            component={RouterLink}
            to={firmFeatureSettingsUrl(firmSlug)}
            data-testid={DAILY_NAV_SETUP_TEST_IDS.goToFeatureSettingsButton}>
            Go to Feature Settings
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default ReviewStep;
